import { Badge } from "#components/ui/badge"
import { Button } from "#components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "#components/ui/table"
import { useCancelInvitation, useResendInvitation } from "#hooks/use-invitations"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import type { AccountInvitationResponse } from "@home-finance-manager/shared"
import { format } from "date-fns"
import { Mail, Trash2 } from "lucide-react"

interface InvitationsTableProps {
  invitations: AccountInvitationResponse[]
}

export function InvitationsTable({ invitations }: InvitationsTableProps) {
  const cancelInvitation = useCancelInvitation()
  const resendInvitation = useResendInvitation()

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "accepted":
        return "success"
      case "expired":
        return "destructive"
      case "cancelled":
        return "destructive"
      default:
        return "secondary"
    }
  }

  const handleCancel = async (id: number) => {
    if (confirm("Are you sure you want to cancel this invitation?")) {
      try {
        await cancelInvitation.mutateAsync(id)
      } catch (error) {
        alert(getUserFriendlyErrorMessage(error))
      }
    }
  }

  const handleResend = async (id: number) => {
    try {
      await resendInvitation.mutateAsync(id)
      alert("Invitation resent successfully")
    } catch (error) {
      alert(getUserFriendlyErrorMessage(error))
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Account</TableHead>
          <TableHead>Invited By</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Invited Date</TableHead>
          <TableHead>Expires Date</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invitations.map((invitation) => (
          <TableRow key={invitation.id}>
            <TableCell>{invitation.email}</TableCell>
            <TableCell>{invitation.account.name}</TableCell>
            <TableCell>{invitation.invitedBy.fullName || invitation.invitedBy.email}</TableCell>
            <TableCell>{invitation.role}</TableCell>
            <TableCell>
              <Badge variant={getStatusBadgeVariant(invitation.status)}>{invitation.status}</Badge>
            </TableCell>
            <TableCell>{format(new Date(invitation.createdAt), "MMM d, yyyy")}</TableCell>
            <TableCell>{format(new Date(invitation.expiresAt), "MMM d, yyyy")}</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {invitation.status === "pending" && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResend(invitation.id)}
                      disabled={resendInvitation.isPending}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleCancel(invitation.id)}
                      disabled={cancelInvitation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
